import {
    Box,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    TextField,
    Button,
    Paper,
    Divider,
    IconButton,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGlobalState } from "../contexts/GlobalState";
import { useEffect, useRef, useState } from 'react';
import { FeedbackType, useSubmitFeedback } from '../api/vegApl';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useCommonStyles } from '../styles/GenericStyle';
import { useNavigate } from 'react-router-dom';
import useGetCurrentUser from '../api/useGetCurrentUser';
import { red } from '../colors';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { ALLOWED_TYPES, CONTACT_INFO, MAX_FILES, MAX_FILE_SIZE_MB } from './feedback/constants';
import { FEEDBACK_EDITOR_FORMATS, FEEDBACK_EDITOR_MODULES } from './feedback/editorConfig';
import { getDescriptionText, getFeedbackErrorMessage, getFeedbackSuccessMessage } from './feedback/helpers';
import {
    attachButtonSx,
    attachedFileChipSx,
    attachedFilesWrapSx,
    backIconSx,
    backLinkSx,
    contactEmailIconSx,
    contactEmailLinkSx,
    contactItemSx,
    contactRowSx,
    contactSubtitleSx,
    contactTitleSx,
    contentPaperSx,
    editorWrapperSx,
    feedbackTypeGroupSx,
    feedbackTypeLabelSx,
    feedbackTypeRadioSx,
    fileRulesTextSx,
    headerWrapperSx,
    leftPaneSx,
    pageRootSx,
    removeFileIconSx,
    rightPaneCardSx,
    rightPaneSx,
    subjectFieldSx,
    submitButtonSx,
    titleSx,
} from './feedback/styles';

export const Feedback = () => {
    const classes = useCommonStyles();
    const navigate = useNavigate();
    const { setBreadcrumbsText, state: globalState } = useGlobalState();
    const { data: currentUser } = useGetCurrentUser(globalState?.userId || '');
    const { showSnackbar } = useSnackbar();
    const [feedbackType, setFeedbackType] = useState<FeedbackType>(FeedbackType.report_issue);
    const [subject, setSubject] = useState<string>('');
    const [descriptionHtml, setDescriptionHtml] = useState<string>('');
    const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
    const [fileError, setFileError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const quillContainerRef = useRef<HTMLDivElement>(null);
    const quillInstanceRef = useRef<Quill | null>(null);

    const { mutate: submitFeedback, isLoading: isSubmitting } = useSubmitFeedback({
        mutation: {
            onSuccess: (response: any) => {
                const successMessage = getFeedbackSuccessMessage(response);

                showSnackbar({ message: successMessage, severity: 'success' });
                setFeedbackType(FeedbackType.report_issue);
                setSubject('');
                setDescriptionHtml('');
                setAttachedFiles([]);
                setFileError('');
                quillInstanceRef.current?.setText('');
            },
            onError: (error: any) => {
                const errorMessage = getFeedbackErrorMessage(error);
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    useEffect(() => {
        setBreadcrumbsText(["Home", "Feedback"]);
    }, [setBreadcrumbsText]);

    useEffect(() => {
        if (!quillContainerRef.current || quillInstanceRef.current) return;

        const quill = new Quill(quillContainerRef.current, {
            theme: 'snow',
            modules: FEEDBACK_EDITOR_MODULES,
            formats: FEEDBACK_EDITOR_FORMATS,
            placeholder: 'Enter Description here',
        });

        quill.on('text-change', () => {
            setDescriptionHtml(quill.root.innerHTML);
        });

        quillInstanceRef.current = quill;
    }, []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileError('');
        const selected = Array.from(e.target.files ?? []);
        const remaining = MAX_FILES - attachedFiles.length;

        const valid: File[] = [];
        for (const file of selected) {
            if (valid.length >= remaining) {
                setFileError(`Max ${MAX_FILES} files allowed.`);
                break;
            }
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                setFileError(`"${file.name}" exceeds ${MAX_FILE_SIZE_MB} MB limit.`);
                continue;
            }
            if (!ALLOWED_TYPES.includes(file.type)) {
                setFileError(`"${file.name}" is not an allowed file type.`);
                continue;
            }
            valid.push(file);
        }
        setAttachedFiles(prev => [...prev, ...valid]);
        // reset input so same file can be re-selected
        e.target.value = '';
    };

    const removeFile = (index: number) => {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index));
        setFileError('');
    };

    const handleSubmit = () => {
        const descriptionText = getDescriptionText(descriptionHtml);
        const descriptionPayload = descriptionHtml?.trim();

        if (!subject.trim()) {
            showSnackbar({ message: 'Please enter a subject.', severity: 'error' });
            return;
        }
        if (!descriptionText) {
            showSnackbar({ message: 'Please enter a description.', severity: 'error' });
            return;
        }

        const userEmail = (globalState?.userId ?? '').trim().toLowerCase();
        const userName =
            (currentUser?.user_name ?? `${globalState?.firstName ?? ''} ${globalState?.lastName ?? ''}`)
                .trim();

        if (!userEmail) {
            showSnackbar({ message: 'Unable to identify current user email.', severity: 'error' });
            return;
        }
        if (!userName) {
            showSnackbar({ message: 'Unable to identify current user name.', severity: 'error' });
            return;
        }

        submitFeedback({
            data: {
                feedback_type: feedbackType,
                subject: subject.trim(),
                description: descriptionPayload,
                user_email: userEmail,
                user_name: userName,
                attachments: attachedFiles,
            },
        });
    };

    return (
        <Box sx={pageRootSx}>
            <Box sx={headerWrapperSx}>
                <Typography
                    component="span"
                    sx={backLinkSx}
                    className={classes.labelNoto16Bold700}
                    onClick={() => navigate(-1)}
                >
                    <ArrowBackIcon sx={backIconSx} />
                    <Typography component="span" className={classes.labelNoto20Bold600}>Back</Typography>
                </Typography>
                <Typography component="h1" className={classes.labelNoto36Bold700} sx={titleSx}>
                    Feedback
                </Typography>
            </Box>

            <Paper
                elevation={2}
                sx={contentPaperSx}
            >
                {/* ── Left: Feedback Form ── */}
                <Box sx={leftPaneSx}>
                    {/* Feedback Type */}
                    <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                        <Typography
                            variant="body2"
                            className={classes.labelInter14Bold400}
                            sx={feedbackTypeLabelSx}
                        >
                            Feedback Type
                        </Typography>
                        <RadioGroup
                            row
                            value={feedbackType}
                            onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                            sx={feedbackTypeGroupSx}
                        >
                            <FormControlLabel
                                value={FeedbackType.report_issue}
                                control={
                                    <Radio sx={feedbackTypeRadioSx} />
                                }
                                slotProps={{ typography: { className: classes.labelInter14Bold400 } }}
                                label="Report Issue"
                            />
                            <FormControlLabel
                                value={FeedbackType.request_information}
                                control={
                                    <Radio sx={feedbackTypeRadioSx} />
                                }
                                slotProps={{ typography: { className: classes.labelInter14Bold400 } }}
                                label="Request Information"
                            />
                            <FormControlLabel
                                value={FeedbackType.left_feedback}
                                control={
                                    <Radio sx={feedbackTypeRadioSx} />
                                }
                                slotProps={{ typography: { className: classes.labelInter14Bold400 } }}
                                label="Left Feedback"
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Subject */}
                    <Typography variant="body2" className={classes.labelInter14Bold400} mb={0.5}>
                        Subject<span style={{ color: red, marginLeft: 4 }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter the Subject here"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        sx={subjectFieldSx}
                        slotProps={{ htmlInput: { className: classes.labelInter16Bold400 } }}
                    />

                    {/* Description */}
                    <Typography variant="body2" className={classes.labelInter14Bold400} mb={0.5}>
                        Description<span style={{ color: red, marginLeft: 4 }}>*</span>
                    </Typography>
                    <Box sx={editorWrapperSx}>
                        <Box ref={quillContainerRef} />
                    </Box>

                    {/* File Upload */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AttachFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        sx={attachButtonSx}
                    >
                        Choose File to Attach (Optional)
                    </Button>

                    <Typography variant="caption" color="text.secondary" display="block" sx={fileRulesTextSx} className={classes.labelInter14Bold400}>
                        Max File limit : {MAX_FILES} | File Size : {MAX_FILE_SIZE_MB} Mb | Allowed File Types : Word, Excel, PPT, PDF, Image
                    </Typography>

                    {/* Attached files list */}
                    {attachedFiles.length > 0 && (
                        <Box sx={attachedFilesWrapSx}>
                            {attachedFiles.map((file, idx) => (
                                <Box key={idx} sx={attachedFileChipSx}>
                                    <Typography variant="caption" className={classes.labelInter14Bold400}>{file.name}</Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => removeFile(idx)}
                                        sx={removeFileIconSx}
                                    >
                                        ✕
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {fileError && (
                        <Typography variant="caption" color="error" display="block" mb={1} className={classes.labelInter14Bold400}>
                            {fileError}
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={submitButtonSx}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </Box>

                {/* ── Right: Contact Information ── */}
                <Box sx={rightPaneSx}>
                    <Box sx={rightPaneCardSx}>
                        <Typography variant="body2" fontWeight={700} mb={0.6} sx={contactTitleSx}>
                            Contact Information
                        </Typography>
                        <Divider sx={{ mb: 1.25 }} />

                        {CONTACT_INFO.map((contact, idx) => (
                        <Box key={idx} sx={contactItemSx(idx, CONTACT_INFO.length)}>
                            <Typography variant="caption" className={classes.labelNoto14Bold700}>
                                    {contact.name}
                                </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={contactSubtitleSx} className={classes.labelInter14Bold400}>
                                    {contact.title}
                                </Typography>
                            <Box sx={contactRowSx}>
                                <EmailIcon sx={contactEmailIconSx} />
                                    <Typography
                                    variant="caption"
                                        component="a"
                                        href={`mailto:${contact.email}`}
                                        className={classes.labelInter14Bold400}
                                        sx={contactEmailLinkSx}
                                    >
                                        {contact.email}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};
